/**
 * Схемы игровых конфигов — содержимое таблицы `game_configs`.
 *
 * Железное правило №3: числа игры живут в базе, а не в коде. Но «жить в базе»
 * без схемы означает «жить нигде»: одна опечатка в админке — и на проде
 * стартовый кошелёк становится строкой, а узнаём мы об этом от первого игрока.
 *
 * Поэтому у каждого ключа есть zod-схема, и она одна на три места:
 *   1. сид `content/economy.json` — падаем на этапе сида, а не в рантайме;
 *   2. чтение конфига в API — значение из базы проверяется при загрузке;
 *   3. форма админки (A1) — та же схема валидирует правку до записи.
 *
 * Соглашение об именах: ключ конфига — snake_case (это идентификатор строки
 * в базе), поля внутри значения — camelCase (это обычный TS-объект).
 */

import { z } from 'zod'
import { MUSEBOX_QUALITY_TIERS } from './generation.js'

/** Целое ≥ 0: валюты и счётчики отрицательными не бывают. */
const count = z.number().int().nonnegative()
/** Целое ≥ 1: интервалы и множители, где ноль лишён смысла. */
const positive = z.number().int().positive()

/** Стартовый кошелёк нового игрока. Начисляется через леджер, не UPDATE. */
export const startingWalletSchema = z
  .object({
    gems: count,
    gold: count,
    energy: count,
  })
  .strict()

/**
 * Энергия. Хранится лениво: текущее значение считается на чтение как
 * `min(cap, energy + floor((now - energyUpdatedAt) / regenSeconds))`.
 *
 * Три числа читаются вместе и только вместе: `cap ÷ battleCost` — это сколько
 * боёв в баке, `cap × regenSeconds` — за сколько бак наполняется с нуля.
 * Калибровка среза: бак = ДВА боя, полное восстановление — СУТКИ (1 ⚡ = бой,
 * cap 2, 12 часов на единицу). Правится из админки, но правится втроём:
 * поднять один `cap`, забыв про `regenSeconds`, значит удлинить сутки.
 */
export const energySchema = z
  .object({
    cap: positive,
    /** Секунд на единицу восстановления. Срез: 12 ч, то есть сутки на бак. */
    regenSeconds: positive,
    /** Списывается на старте боя. Срез: 1 ⚡, то есть «бак = число боёв». */
    battleCost: count,
    /**
     * Возвращается при поражении. Ноль на срезе, и это калибровка, а не
     * забытая ручка: пока бак был на пять боёв, возврат берёг игрока от
     * стены уровня, а на баке в два боя он означает «проигрыш не считается
     * матчем» — то есть безлимитные попытки для того, кто проигрывает.
     */
    defeatRefund: count,
    /**
     * Цена полного бака в самоцветах. 0 — покупка выключена, и клиент
     * не показывает кнопку: кнопка, которая ничего не покупает, хуже её
     * отсутствия.
     *
     * Число здесь, а не в коде, ровно по правилу №3: это ручка экономики,
     * и крутить её будут чаще всего остального в этом файле.
     */
    refillGems: count,
    /**
     * Цена ОДНОЙ единицы энергии в самоцветах. 0 — покупка выключена.
     *
     * Заведена ради удержания новичка, и это главное, что о ней надо знать.
     * Полный бак стоит весь стартовый кошелёк, поэтому человек, выигравший
     * два первых боя и захотевший третий, выбирал между «отдать всё»
     * и «прийти завтра» — оба варианта проигрышные. Поштучная докупка
     * превращает стартовые 50 💎 в пять «продолжить»: их ровно хватает,
     * чтобы пройти обучающие уровни за один вечер и привыкнуть платить
     * за темп раньше, чем начнётся настоящая сложность.
     *
     * Читается ВМЕСТЕ с `refillGems`: если бак дороже, чем `cap` штук
     * поштучно, кнопка «полный бак» становится заведомо худшей из двух,
     * и это ловушка для игрока, а не выбор. На срезе 10 против 18 при
     * баке в две единицы — мелкая скидка за оптом. Проверку держит
     * `.refine` ниже.
     */
    continueGems: count.default(0),
  })
  .strict()
  .refine((v) => v.defeatRefund <= v.battleCost, {
    message: 'возврат энергии при поражении не может превышать стоимость боя',
  })
  .refine((v) => v.refillGems === 0 || v.continueGems === 0 || v.refillGems <= v.continueGems * v.cap, {
    message:
      'полный бак дороже, чем набрать столько же поштучно: одна из двух кнопок покупки станет ловушкой',
  })

/** Награда за победу: золото, опыт и симпатия. */
const winRewardSchema = z
  .object({
    gold: count,
    xp: count,
    affinity: count,
  })
  .strict()

/**
 * Награда за поражение: утешительные золото с опытом — и ШТРАФ симпатии.
 *
 * Прибавки симпатии здесь нет и быть не может, а вот вычет есть. Разница
 * не в знаке, а в том, что каждое из двух чисел делает с экономикой.
 *
 * ПОЧЕМУ НЕ ПРИБАВКА. Проигранный бой не дороже выигранного (`defeatRefund`
 * возвращает часть цены), поэтому любая симпатия за поражение идёт за единицу
 * энергии не медленнее, чем за победу: слить бой на первом ходу становится
 * самым быстрым способом открыть сцену. С золотом и опытом этого не бывает —
 * их поражение платит в разы хуже победы, поэтому утешительный приз остаётся.
 *
 * ПОЧЕМУ ШТРАФ. Отношения, которые только растут, — это не отношения, а
 * счётчик боёв: ассистент становится ближе к игроку, который у неё на глазах
 * раз за разом терял базу. Со штрафом победа обязана перевешивать поражение,
 * то есть шкала симпатии измеряет игру, а не время в игре.
 *
 * Число НЕОТРИЦАТЕЛЬНОЕ и вычитается кодом (`resolveRewards`), а не хранится
 * со знаком. Знак в форме админки — это способ однажды выставить `-1` в поле
 * «штраф» и получить бой, который платит симпатию за поражение: ровно тот
 * фарм, ради запрета которого поле и заведено.
 *
 * Штраф ассистенту НЕ умножается на `assistantAffinityMultiplier` — см. `grantAffinity`.
 *
 * Имя поля новое (`affinityPenalty`, а не прежнее `affinity`) и разобрано
 * в двух местах не случайно. Строку, переведённую в `source='admin'`, сид
 * не перезаписывает: на живой базе в `loss` может лежать `affinity: 1` от
 * прежней формы конфига, а `.strict()` отвечает на лишний ключ ошибкой.
 * Поэтому `preprocess` старое поле снимает, а новое приходит с `.default(0)`:
 * выкатка на такой базе означает «штрафа пока нет», а не упавшее чтение
 * `battle_rewards`, без которого не поднимается `main.ts`.
 */
const lossRewardSchema = z.preprocess(
  (raw) => {
    if (raw === null || typeof raw !== 'object') return raw
    const rest: Record<string, unknown> = { ...(raw as Record<string, unknown>) }
    delete rest['affinity']
    return rest
  },
  z
    .object({
      gold: count,
      xp: count,
      /** Сколько 💜 отнимает поражение. 0 — штрафа нет. Ниже нуля не бывает. */
      affinityPenalty: count.default(0),
    })
    .strict(),
)

/** Награды за бой. Уровень может их переопределить своими полями reward_*. */
export const battleRewardsSchema = z
  .object({
    win: winRewardSchema,
    loss: lossRewardSchema,
    /** Симпатия ассистенту идёт с множителем — ради этого ассистента и выбирают. */
    assistantAffinityMultiplier: positive,
    /** XP на один уровень аккаунта; отсутствие сохраняет старый режим. */
    accountXpPerLevel: positive.optional(),
    /** Срок боя в часах от старта. У боя, начатого до появления срока (он без `expires_at`), срок считается от `created_at`. */
    abandonAfterHours: positive.max(8760).optional(),
  })
  .strict()

/* ─────────────────────────── тон ответа в диалоге ───────────────────────── */

/**
 * Чем ответ ЗВУЧИТ. Тон пишется рядом с репликой в `chat-scripts.json`
 * и не несёт числа: число даёт пара «тон × кому сказано».
 *
 * Четыре, а не десять: тон должен различаться на слух автора реплик, иначе
 * банк заполняется наугад и матрица вкусов калибрует шум.
 *
 *  • `cool`    — сухо, по делу, без тепла: «Let's just get this over with.»
 *  • `warm`    — забота и поддержка: «Mine too. We'll manage.»
 *  • `bold`    — прямо и смело, без страховки: «Open it.»
 *  • `playful` — шутка, поддразнивание: «Double it.»
 */
export const DIALOGUE_TONES = ['cool', 'warm', 'bold', 'playful'] as const
export type DialogueTone = (typeof DIALOGUE_TONES)[number]

/** Целое со знаком: прибавка за тон бывает и отрицательной. */
const signed = z.number().int()

/** Вкус к тонам: сколько 💜 даёт каждый. Пропущенный тон — ноль. */
const toneMap = z.record(z.enum(DIALOGUE_TONES), signed)

/**
 * Настроение героини: чем закончился ПРОШЛЫЙ бой игрока. `none` — прошлого
 * боя ещё не было.
 */
export const BATTLE_MOODS = ['won', 'lost', 'none'] as const
export type BattleMood = (typeof BATTLE_MOODS)[number]

/**
 * ВКУС ГЕРОИНИ К ТОНУ ОТВЕТА — то, из-за чего один и тот же выбор одной
 * прибавляет симпатию, а другой отнимает.
 *
 * Зачем это вообще. Пока прибавку давал НОМЕР варианта (`dialogueChoices`),
 * правильный ответ был один и тот же всегда: последний. Игрок находил это
 * за два боя, дальше жал третью кнопку не читая, и развилка переставала
 * быть выбором — оставалась кнопка «+2 💜» с текстом сверху.
 *
 * Теперь прибавку решают три вещи, и ни одна не видна до нажатия:
 *
 *  1. **Тон ответа** — как он звучит (`chat-scripts.json`, поле `tone`).
 *  2. **Кому сказано** (`byArchetype`) — дерзкой хочется заботы, тихой
 *     хочется смелости. Матрица намеренно НЕ симметрична и намеренно
 *     содержит минусы: ответ не в её вкусе отношения портит, а не просто
 *     не улучшает.
 *  3. **Чем кончился прошлый бой** (`afterBattle`) — та же фраза той же
 *     девушке звучит по-разному, когда она только что выиграла и когда
 *     проиграла. Это и есть защита от заучивания: пара «тон × героиня»
 *     заучивается за вечер, тройка с меняющимся третьим членом — нет.
 *
 * Итог: `clamp(byArchetype[архетип][тон] + afterBattle[исход][тон], min, max)`.
 *
 * Ключ `byArchetype` — архетип из `heroines.json` (он же `persona_id` банка
 * реплик). Архетипа нет в матрице — считаем по-старому, по номеру варианта
 * из `dialogueChoices`: новая героиня из админки обязана начать работать
 * до того, как ей пропишут вкус, а не после.
 */
export const dialogueTonesSchema = z
  .object({
    /**
     * Границы итоговой прибавки. `min` отрицательный: без него «ответ может
     * отнять симпатию» не имеет способа сбыться.
     *
     * Симпатия при этом ниже нуля не уходит никогда — это уже забота
     * `addAffinity`, там стоит `greatest(0, …)` и то же условие в базе.
     */
    min: signed,
    max: signed,
    /** Вкус архетипа: тон → 💜. Ключ — `heroines.json` archetype. */
    byArchetype: z.record(z.string(), toneMap),
    /** Поправка на исход прошлого боя. Складывается со вкусом. */
    afterBattle: z.record(z.enum(BATTLE_MOODS), toneMap),
  })
  .strict()
  .refine((v) => v.min <= v.max, { message: 'min больше max' })

/**
 * Симпатия. Порог открытия сцены здесь НЕ живёт: он у шаблона сцены
 * (`scene_templates.affinity_required`), потому что порог у каждой сцены свой,
 * а конфиг — глобальный. Второе место правды для одного числа — верный способ
 * однажды выкатить главу, где порог в базе и порог в конфиге разошлись.
 */
export const affinitySchema = z
  .object({
    /**
     * Прибавка за вариант ответа ПО НОМЕРУ — запасной путь и он же мерка
     * длины развилки.
     *
     * Основной путь теперь `dialogueTones`: по номеру считается только та
     * героиня, чьего архетипа нет в матрице вкусов. Длину сид по-прежнему
     * сверяет с числом вариантов в банке — это единственная проверка,
     * ловящая четвёртый дописанный вариант.
     */
    dialogueChoices: z.array(count).min(2),
    /**
     * Вкус к тону ответа: из-за него один и тот же выбор одной героине
     * нравится, а другой нет. Разбор — в шапке `dialogueTonesSchema`.
     *
     * `.default()`, а не обязательное поле, по той же причине, что
     * у `sceneWatch`: строка `affinity` уже лежит в живых базах, и если
     * её перевели в `source='admin'`, сид её не перезапишет. Без умолчания
     * выкатка уронила бы чтение конфига, а с ним — и профиль героини,
     * и старт боя.
     *
     * Умолчание пустое: пустая матрица означает «архетипов в ней нет»,
     * то есть ровно прежнее поведение по номеру варианта.
     */
    dialogueTones: dialogueTonesSchema.default({
      min: -3,
      max: 6,
      byArchetype: {},
      afterBattle: {},
    }),
    /**
     * Дейлик «внимание». Сбрасывается в полночь UTC, а не по скользящему
     * окну в 24 часа.
     *
     * Ручки «сколько часов кулдаун» здесь намеренно нет, хотя план §5
     * говорит «раз в 24 часа». Причина: защита от двойного получения стоит
     * на первичном ключе `daily_claims (user_id, kind, ref_id, claim_day)` —
     * то есть на календарном дне. Ключ гоняет и параллельные запросы, чего
     * сравнение таймстампов в коде не даёт. Держать при этом в конфиге
     * число часов означало бы обещать поведение, которого нет: клейм
     * в 23:59 и в 00:01 прошли бы оба, с разницей в две минуты.
     */
    dailyAttention: count,
    /**
     * Прибавка за ПЕРВЫЙ досмотр сцены (E8, `POST /scenes/watched`). Повторы
     * бесплатны и симпатии не дают: иначе луп финального клипа превращается
     * в кнопку «нажми ещё раз», и вся калибровка порогов теряет смысл.
     *
     * `.default()`, а не обязательное поле: строка `affinity` уже лежит
     * в базах — и в проде, и у владельца локально, — а строку, переведённую
     * в `source='admin'`, сид не перезапишет. Без умолчания выкатка E8
     * уронила бы чтение конфига на живой базе, и упало бы всё, что его
     * читает, включая профиль героини.
     *
     * Умолчание — единица, а не прежняя пятёрка: досмотр сцены платит
     * симпатию за то, что симпатией уже оплачено, и на порогах, разнесённых
     * от 4 до 120 (`scene-template.json`), пятёрка за просмотр означала
     * заметный шаг к следующей сцене просто за нажатие «play».
     */
    sceneWatch: count.default(1),
  })
  .strict()

/**
 * Скриптовый чат ассистента (E9, GDD §2.3).
 *
 * Оба числа здесь — пороги, а не прибавки: прибавки за реплики живут
 * в `affinity.dialogueChoices`, потому что это симпатия, и калибруется она
 * вместе с остальной симпатией, а не вместе с расписанием сообщений.
 */
export const chatSchema = z
  .object({
    /**
     * Через сколько часов молчания ассистент пишет «возвращайся». План §7
     * и GDD §2.3 называют 20 часов — то есть чуть раньше суток, чтобы
     * сообщение заставало игрока в то же время дня, что и вчера.
     *
     * Проверка стоит на входе игрока (`GET /me`), а не в cron: пуши
     * и фоновые задания подключаются после среза, и до тех пор «в момент
     * входа» — единственный момент, когда мы про игрока вообще знаем.
     */
    comebackHours: positive,
    /**
     * Ширина коридора «мы стали ближе»: за сколько пунктов до порога сцены
     * ассистент заговаривает о ней. План: 1–4 пункта.
     *
     * Ровно коридор, а не «меньше порога»: сообщение «ещё чуть-чуть»
     * игроку с 1 💜 из 10 — это не подводка к подарку, а враньё, и второй
     * раз он его не прочитает.
     */
    affinityCloseWindow: positive,
    /** Слот сцены уровня → точная строка банка послебоевого разговора. */
    battleScriptBySceneSlot: z.record(z.string().regex(/^\d+$/), count).optional(),
    /** Нейтральная строка, когда для слота нет отдельного разговора. */
    fallbackBattleScriptOrd: count.optional(),
  })
  .strict()

/**
 * Генерация роликов у партнёра (Musebox API v1).
 *
 * Числа тут не игровые, а расходные — и живут они в конфиге ровно поэтому.
 * Каждая генерация стоит партнёрских кредитов, то есть денег; ручка,
 * которой это регулируется, обязана крутиться из админки в ту минуту,
 * когда счёт побежал не туда, а не в ближайшую выкатку.
 *
 * САМАЯ ВАЖНАЯ СТРОКА ЗДЕСЬ — `autoGenerate`. Умолчание СХЕМЫ осталось
 * `false`: оно срабатывает там, где ключа нет вовсе (старая база, чужой
 * конфиг, тест), и в такой ситуации не заказывать безопаснее, чем
 * заказывать. Поставка игры (`content/economy.json`) с E10 ставит `true`
 * осознанно — расход к тому времени стало чем ограничить (шаблон
 * назначается по одному из админки, готовый ролик уходит в кэш,
 * `maxInFlight` и `maxCreditsPerJob` держат объём и цену, а клип с
 * отказом автозаказ больше не трогает).
 *
 * Пока флаг false, интеграция принимает результаты, сверяется поллингом и
 * складывает файлы к нам, но САМА НИЧЕГО НЕ ЗАКАЗЫВАЕТ: задачи ставятся
 * руками (`pnpm musebox generate …`) и из админки.
 */
export const museboxSchema = z
  .object({
    /**
     * Заказывать ли ролик автоматически при промахе кэша. false — сцена
     * без клипа просто показывает «она готовится…», и заказ остаётся
     * ручным действием человека. Умолчание схемы (ключа нет вовсе) —
     * false; поставка в `content/economy.json` — true, см. комментарий
     * к схеме выше.
     */
    autoGenerate: z.boolean().default(false),
    /** Тир качества. Дороже — не значит лучше для лупа на 5 секунд. */
    quality: z.enum(MUSEBOX_QUALITY_TIERS).default('fast'),
    /**
     * Длительность ролика. 7 и 10 доступны не у всех шаблонов, поэтому
     * перед отправкой она сверяется с каталогом: партнёр отвечает `400`
     * на неподдерживаемую длительность, и ловить это на живом заказе
     * незачем.
     */
    durationS: z
      .union([z.literal(5), z.literal(7), z.literal(10)])
      .default(5),
    /** Звуковая дорожка. Требует capability `audio` у плана, иначе `403`. */
    addAudio: z.boolean().default(false),
    /**
     * Сколько наших задач может висеть у партнёра одновременно.
     *
     * Держится НАМИ, а не их лимитом: превышение `plan.concurrentJobs`
     * отвечает `429` и задачу не создаёт, то есть очередь на их стороне
     * молча теряет заказы, а мы считаем, что отправили. Значение ставится
     * не выше `plan.concurrentJobs` из `GET /api/v2/account`.
     */
    maxInFlight: positive.default(3),
    /**
     * Потолок цены ОДНОЙ задачи в кредитах. Считается до отправки
     * (`POST /api/v2/jobs/estimate`); дороже — задача не уходит и падает
     * с внятной ошибкой.
     *
     * Предохранитель от опечатки в качестве и длительности: `hd` + 10 с
     * стоит вчетверо против `fast` + 5 с, и обнаруживается такая правка
     * по счёту за месяц.
     */
    maxCreditsPerJob: positive.default(120),
    /**
     * Через сколько минут молчания идти спрашивать статус самому.
     *
     * Вебхук — доставка best effort (§6 их документации), и единственный
     * способ отличить «ещё считает» от «колбэк потерян» — сверка
     * поллингом. Их типичная генерация — десятки секунд, поэтому 15 минут
     * это уже точно не «долго считает».
     */
    stuckAfterMinutes: positive.default(15),
    /**
     * ПРЕДОХРАНИТЕЛЬ, А НЕ ЭКОНОМИЧЕСКИЙ РЫЧАГ: сколько задач в сутки
     * автоматика заказывает ВСЕГО.
     *
     * За ролики платит игрок энергией — заход в уровень стоит энергии,
     * энергия покупается за самоцветы, и ролик заказывается ровно за
     * потраченную энергию (решение владельца 02.09.2026). Ограничивать
     * игроков этим числом не нужно и не задумано.
     *
     * Он существует ради другого: ошибка в коде, бот или всплеск
     * регистраций не должны обнулить баланс у партнёра за ночь. Тысяча
     * кредитов — это 33–50 роликов, и восстановить их можно только
     * покупкой. Поэтому число ставится ЗАВЕДОМО ВЫШЕ нормального
     * расхода: упереться в него — событие, а не будни.
     *
     * 0 выключает автозаказ полностью. Игрок при этом видит «она
     * готовится…», а не отказ: ролик появится, когда потолок отпустит.
     */
    maxJobsPerDay: count.default(500),
  })
  .strict()

/**
 * Ассистентка игрока: что стоит завести её заново.
 *
 * Отдельный ключ, а не поле в `energy`, потому что `energy` — про темп игры
 * (сколько боёв в сутки и почём их докупить), а это про внешность героини.
 * Смешав их, однажды подвинем цену боя, правя цену пересоздания.
 */
export const assistantSchema = z
  .object({
    /**
     * Цена ПЕРЕСОЗДАНИЯ в самоцветах. Первая ассистентка бесплатна всегда:
     * без неё играть не во что, и платная стена до первого экрана убивает
     * новичка вернее любой цены.
     *
     * 0 — пересоздание бесплатно. Выключить его совсем этим числом нельзя
     * и не нужно: человек, которому не нравится своя героиня, иначе просто
     * уходит.
     */
    recreateGems: count,
    /**
     * Цена ОДНОЙ попытки собрать внешность в конструкторе. Бесплатной
     * попытки нет (решение владельца 17.09.2026): каждая стоит партнёру
     * 18 кредитов, лицо каждый раз новое, и перебирать варианты игрок
     * будет. Провал у партнёра самоцветы возвращает.
     *
     * Не ниже единицы: бесплатная кнопка, за которой живые деньги у
     * партнёра, — это кнопка «обнулить баланс» для первого же бота.
     *
     * Умолчание здесь, а не только в файле: строка `assistant` на проде
     * может принадлежать админке, сид её не перепишет, а `.strict()` без
     * умолчания уронил бы чтение конфига — и старт `main.ts` вместе с ним.
     */
    portraitGems: positive.default(10),
  })
  .strict()

/**
 * Реестр: ключ → схема. Единственный источник правды о том, какие конфиги
 * существуют. Админка строит по нему список разделов, сид — список строк.
 */
export const GAME_CONFIG_SCHEMAS = {
  starting_wallet: startingWalletSchema,
  energy: energySchema,
  battle_rewards: battleRewardsSchema,
  affinity: affinitySchema,
  chat: chatSchema,
  musebox: museboxSchema,
  assistant: assistantSchema,
} as const

export type GameConfigKey = keyof typeof GAME_CONFIG_SCHEMAS

export type GameConfigValue<K extends GameConfigKey> = z.infer<(typeof GAME_CONFIG_SCHEMAS)[K]>

export const GAME_CONFIG_KEYS = Object.keys(GAME_CONFIG_SCHEMAS) as GameConfigKey[]

export function isGameConfigKey(key: string): key is GameConfigKey {
  return Object.hasOwn(GAME_CONFIG_SCHEMAS, key)
}

/**
 * Разбор значения конфига. Бросает с указанием ключа: сообщение zod само по
 * себе не говорит, какой из четырёх конфигов сломан.
 */
export function parseGameConfig<K extends GameConfigKey>(key: K, value: unknown): GameConfigValue<K> {
  const result = GAME_CONFIG_SCHEMAS[key].safeParse(value)
  if (!result.success) {
    const details = result.error.issues
      .map((i) => `${i.path.join('.') || '<корень>'}: ${i.message}`)
      .join('; ')
    throw new Error(`game_configs.${key}: ${details}`)
  }
  return result.data as GameConfigValue<K>
}
